/**
 * Zone slider — a single-value slider whose track is split into coloured
 * zones (approval levels, risk bands, price corridors…). Pure types and maths;
 * no Angular here so the Design Builder's inspector, the sample-data generator
 * and the component itself all share ONE definition of what a zone is.
 *
 * The `[data]` object is FLAT (like the KPI card and the other input
 * controls): every key below can be set statically, and the Design Builder's
 * data tier overrides `value` / `min` / `max` / `cost` / `zones` from a
 * dataset at render time.
 */

import { formatNumber } from '../../self-service-visualization/shared/number-format.utils';

/** One coloured band of the track. */
export interface ZoneSliderZone {
  /** Shown in the badge while the value sits inside this zone. */
  label?: string;
  /** Any CSS colour. Missing → picked from {@link ZONE_SLIDER_PALETTE} by position. */
  color?: string;
  /** Lower bound. Missing → the previous zone's `to` (or the slider minimum). */
  from?: number | null;
  /** Upper bound. Missing → the next zone's `from` (or the slider maximum). */
  to?: number | null;
}

export interface ZoneSliderData {
  title?: string;
  /** Optional caption above the control. */
  label?: string;
  /** Renders a `*` after the caption. Presentation only. */
  mandatory?: boolean;
  value?: number | null;
  min?: number | null;
  max?: number | null;
  step?: number | null;
  /** Number formatting of the value and the min / max labels. */
  prefix?: string;
  suffix?: string;
  /** `numberFormats` key from the builder config (NONE | THOUSANDS | COMPACT | …). */
  numberFormat?: string;
  decimals?: number | null;
  /**
   * Secondary text inside the value pill. Tokens: `{value}` `{min}` `{max}`
   * `{cost}` `{pct}` (position in the range) `{margin}` ((value − cost) / value).
   * Hidden when it needs a token that cannot be computed (no cost → no margin).
   */
  subLabel?: string;
  /** Cost basis for `{margin}`. */
  cost?: number | null;
  /** Margin % from a bound column (27 or 0.27 both mean 27%); wins over `cost`. */
  margin?: number | null;
  /** Margin formula text (Design tab); the host turns it into `marginFn`. */
  marginFormula?: string;
  /** Margin % for the handle at `value` (from the margin formula); wins over everything. */
  marginFn?: (ctx: { value: number; min: number; max: number; cost: number | null }) => number | null;
  zones?: ZoneSliderZone[];
  showValue?: boolean;
  showBadge?: boolean;
  /** Fixed badge text. Empty → the active zone's label. */
  badgeText?: string;
  showRangeLabels?: boolean;
  /** The number at every block boundary under the track (min, each level, max). */
  showBoundaryValues?: boolean;
  pillBackground?: string;
  pillColor?: string;
  subLabelColor?: string;
  thumbColor?: string;
  thumbBorderColor?: string;
  trackHeight?: number | null;
  fontFamily?: string;
  fontSize?: number | null;
  disabled?: boolean;
  [key: string]: unknown;
}

export interface ResolvedZone {
  label: string;
  color: string;
  from: number;
  to: number;
  /** Left edge and width as a percentage of the track. */
  leftPct: number;
  widthPct: number;
  active: boolean;
}

export interface ZoneSliderModel {
  min: number;
  max: number;
  step: number;
  value: number;
  /** 0–100, where the thumb sits. */
  thumbPct: number;
  zones: ResolvedZone[];
  activeZone: ResolvedZone | null;
  valueText: string;
  subLabelText: string;
  minText: string;
  maxText: string;
  badgeText: string;
  badgeColor: string;
  /** Every block boundary (min, each level's end, max) with its position on the track. */
  boundaries: Array<{ value: number; pct: number; text: string }>;
}

/** Zone colours by position when a zone (or a dataset row) names none. */
export const ZONE_SLIDER_PALETTE: readonly string[] = [
  '#8f1d1d', '#c9a227', '#3f9d4b', '#3b7ddd', '#7a5af8', '#0f766e', '#db6b1d', '#64748b',
];

/**
 * Defaults, shared with the Design Builder's appearance spec so a freshly
 * dropped widget and a bare `[data]="{}"` render the same thing: a pricing
 * corridor with four approval levels.
 */
export interface ZoneSliderDefaults {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  prefix: string;
  suffix: string;
  numberFormat: string;
  decimals: number;
  subLabel: string;
  cost: number;
  showValue: boolean;
  showBadge: boolean;
  badgeText: string;
  showRangeLabels: boolean;
  pillBackground: string;
  pillColor: string;
  subLabelColor: string;
  thumbColor: string;
  thumbBorderColor: string;
  trackHeight: number;
  fontFamily: string;
  fontSize: number;
  zones: ZoneSliderZone[];
}

export const ZONE_SLIDER_DEFAULTS: ZoneSliderDefaults = {
  label: '',
  value: 8300,
  min: 6800,
  max: 9100,
  step: 100,
  prefix: '$',
  suffix: '',
  numberFormat: 'THOUSANDS',
  decimals: 0,
  subLabel: '[{margin}% Margin]',
  cost: 6059,
  showValue: true,
  showBadge: true,
  badgeText: '',
  showRangeLabels: true,
  pillBackground: '#1e293b',
  pillColor: '#ffffff',
  subLabelColor: '#93c5fd',
  thumbColor: '#ffffff',
  thumbBorderColor: '#1e293b',
  trackHeight: 8,
  fontFamily: '',
  fontSize: 13,
  zones: [
    { label: 'Level 3 — VP Approval', to: 7600, color: '#8f1d1d' },
    { label: 'Level 2 — Director Approval', to: 8400, color: '#c9a227' },
    { label: 'Level 1 — Manager Approval', to: 8900, color: '#3f9d4b' },
    { label: 'Auto-Approved', to: null, color: '#3b7ddd' },
  ],
};

/** Zones can arrive as JSON from a config file or a dataset; keep only sane rows. */
export function normaliseZones(raw: unknown): ZoneSliderZone[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((zone): zone is Record<string, unknown> => !!zone && typeof zone === 'object')
    .map((zone) => ({
      label: typeof zone['label'] === 'string' ? zone['label'] : '',
      color: typeof zone['color'] === 'string' && zone['color'].trim() ? zone['color'].trim() : undefined,
      from: finiteOrNull(zone['from']),
      to: finiteOrNull(zone['to']),
    }));
}

/**
 * Everything the template draws, from the `[data]` object plus the value the
 * user is currently dragging (`currentValue` wins over `data.value`).
 */
export function resolveZoneSliderModel(data: ZoneSliderData, currentValue?: number | null): ZoneSliderModel {
  const d = ZONE_SLIDER_DEFAULTS;
  let min = finiteOr(data.min, d.min);
  let max = finiteOr(data.max, d.max);
  // The track always reaches the value the data gives (e.g. a set price above
  // the last level): clamped, the handle sat at the end and the pill showed the
  // range's end instead of the real number. Only the data value widens it - a
  // value being dragged stays inside, so the track never rescales mid-drag.
  const dataValue = finiteOrNull(data.value);
  if (dataValue !== null && (data.min !== undefined || data.max !== undefined)) {
    min = Math.min(min, dataValue);
    max = Math.max(max, dataValue);
  }
  if (max <= min) max = min + 1;
  const step = Math.max(finiteOr(data.step, d.step), 0) || 1;
  // No value given: the stock example value when the range is the stock one
  // too, otherwise the start of whatever range WAS given.
  const stockRange = data.min === undefined && data.max === undefined;
  const value = clamp(finiteOr(currentValue ?? data.value, stockRange ? d.value : min), min, max);
  const span = max - min;
  const thumbPct = ((value - min) / span) * 100;

  const zones = resolveZones(normaliseZones(data.zones ?? d.zones), min, max, value);
  const activeZone = zones.find((zone) => zone.active) ?? null;

  const format = (n: number) => formatZoneValue(n, data);
  const badgeText = str(data.badgeText) || activeZone?.label || '';
  // `undefined` means "not configured" → stock default; an explicit null means
  // "there is no cost", which hides the margin line.
  const cost = data.cost === undefined ? d.cost : finiteOrNull(data.cost);
  const fixedMargin = finiteOrNull(data.margin);
  const columnMargin = fixedMargin === null ? null : Math.abs(fixedMargin) <= 1 ? fixedMargin * 100 : fixedMargin;
  const formulaMargin = typeof data.marginFn === 'function' ? data.marginFn({ value, min, max, cost }) : null;
  const margin = formulaMargin ?? columnMargin;

  return {
    min, max, step, value, thumbPct, zones, activeZone,
    valueText: format(value),
    subLabelText: renderSubLabel(data.subLabel === undefined ? d.subLabel : str(data.subLabel), { value, min, max, span, cost, margin, format }),
    minText: format(min),
    maxText: format(max),
    badgeText,
    badgeColor: activeZone?.color ?? '#64748b',
    boundaries: zoneBoundaries(zones, min, max).map((point) => ({ value: point, pct: ((point - min) / span) * 100, text: format(point) })),
  };
}

/** Min, where each block ends, and max - each once, in order. */
export function zoneBoundaries(zones: ResolvedZone[], min: number, max: number): number[] {
  const points = [min, ...zones.flatMap((zone) => [zone.from, zone.to]), max];
  return Array.from(new Set(points.filter((point) => Number.isFinite(point) && point >= min && point <= max))).sort((a, b) => a - b);
}

/** Prefix + formatted number + suffix, with the builder's shared number formats. */
export function formatZoneValue(n: number, data: ZoneSliderData): string {
  const d = ZONE_SLIDER_DEFAULTS;
  const decimals = finiteOrNull(data.decimals ?? d.decimals);
  const body = formatNumber(n, str(data.numberFormat) || d.numberFormat, decimals ?? undefined);
  return `${data.prefix ?? d.prefix}${body}${data.suffix ?? d.suffix}`;
}

/**
 * Lay the zones out along [min, max]. Bounds cascade: a zone without `from`
 * starts where the previous one ended; one without `to` runs to the next
 * zone's `from` or to the maximum. Zones that end up empty are dropped.
 */
export function resolveZones(zones: ZoneSliderZone[], min: number, max: number, value: number): ResolvedZone[] {
  const span = Math.max(max - min, Number.EPSILON);
  const out: ResolvedZone[] = [];
  let cursor = min;

  zones.forEach((zone, index) => {
    const from = clamp(zone.from ?? cursor, min, max);
    const nextFrom = zones.slice(index + 1).map((z) => z.from).find((f): f is number => Number.isFinite(f as number));
    const to = clamp(zone.to ?? nextFrom ?? max, min, max);
    cursor = Math.max(cursor, to);
    if (to <= from) return;
    const last = index === zones.length - 1;
    out.push({
      label: zone.label ?? '',
      color: zone.color ?? ZONE_SLIDER_PALETTE[index % ZONE_SLIDER_PALETTE.length],
      from,
      to,
      leftPct: ((from - min) / span) * 100,
      widthPct: ((to - from) / span) * 100,
      active: value >= from && (last ? value <= to : value < to),
    });
  });

  // A value past the final zone still belongs to it (rounding, or a last zone
  // that ends before the maximum).
  if (out.length && !out.some((zone) => zone.active) && value >= out[out.length - 1].to) out[out.length - 1].active = true;
  return out;
}

interface SubLabelContext {
  value: number;
  min: number;
  max: number;
  span: number;
  cost: number | null;
  /** Margin % from a bound column; null → computed from cost. */
  margin?: number | null;
  format: (n: number) => string;
}

/** Fill the sub-label template; '' when a required token has no data behind it. */
export function renderSubLabel(template: string, ctx: SubLabelContext): string {
  if (!template.trim()) return '';
  const needsMargin = template.includes('{margin}');
  const needsCost = template.includes('{cost}');
  const fixedMargin = ctx.margin ?? null;
  if (needsCost && ctx.cost === null) return '';
  if (needsMargin && fixedMargin === null && (ctx.cost === null || ctx.value === 0)) return '';

  const margin = fixedMargin ?? (ctx.cost === null || ctx.value === 0 ? 0 : ((ctx.value - ctx.cost) / ctx.value) * 100);
  const pct = (Math.max(0, ctx.value - ctx.min) / ctx.span) * 100;
  return template
    .replace(/\{value\}/g, ctx.format(ctx.value))
    .replace(/\{min\}/g, ctx.format(ctx.min))
    .replace(/\{max\}/g, ctx.format(ctx.max))
    .replace(/\{cost\}/g, ctx.cost === null ? '' : ctx.format(ctx.cost))
    .replace(/\{pct\}/g, pct.toFixed(0))
    .replace(/\{margin\}/g, margin.toFixed(1));
}

export function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

export function finiteOr(value: unknown, fallback: number): number {
  const parsed = typeof value === 'string' && value.trim() === '' ? NaN : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function finiteOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}
